import { Spacing } from '@persona-id/design-tokens';
import { useContext } from 'react';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';
import Markdown from 'shared/components/Markdown';
import styled, { ThemeContext } from 'styled-components';

import IconCircleBullet from 'images/icons/circle-bullet.svg';
import IconCamera from 'images/icons/inquiry/camera.svg';
import ImageDocument from 'images/pictographs/document.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import VerificationHero from 'inquiry/components/VerificationHero';
import { DocumentCaptureOption, DocumentConfig } from 'lib/interfaces/documentConfig';
import { PictographPosition } from 'lib/interfaces/ThemeSerialized';
import { assertNever } from 'lib/utils/types';
import ButtonInputCapture from 'verify/components/ButtonInputCapture';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import CustomizablePictograph from 'verify/components/CustomizablePictograph';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer/PositionableButtonContainer';
import ThemedParagraph from 'verify/components/ThemedParagraph';
import ThemedTitle from 'verify/components/ThemedTitle';
import PromptOptions from 'verify/lib/interfaces/PromptOptions';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const ACCEPTED_FILE_FORMATS = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'];

const Container = styled('div')``;

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${Spacing[6]};
`;

const CaptureOptionsContainer = styled.div`
  width: 100%;
`;

const OptionButton = styled(Button)`
  margin-bottom: ${Spacing[4]};
  :last-of-type {
    margin-bottom: 0;
  }
`;

const ImgCamera = styled(IconCamera)`
  vertical-align: bottom;

  path[stroke] {
    stroke: ${(props) => props.theme.colorFontButtonPrimary};
  }
`;

const DocumentExampleList = styled('div')`
  margin: ${Spacing[4]} 0 ${Spacing[6]} 0;

  padding: 0 ${Spacing[2]} 0 ${Spacing[2]};
`;

const DocumentExample = styled('div')`
  display: flex;

  margin-bottom: ${Spacing[4]};
`;

const IconContainer = styled('div')`
  padding: 4px ${Spacing[2]} 0 0;
  position: relative;
`;

const StyledCustomizablePictograph = styled(CustomizablePictograph)`
  width: 100%;
`;

export interface VerifyDocumentsPromptViewProps {
  documentConfig: DocumentConfig;
  documentPromptCaptureButton?: string;
  isProcessing: PromptOptions;
  handleDrop: (acceptedFiles: File[], rejectedFiles: FileRejection[], event: DropEvent) => void;
  handleDropRejected: (rejectedFiles: FileRejection[]) => void;
  handleSelectCamera: () => void;
  handleInputCapture: (file: Blob) => void;
  handleDeviceHandoff: () => void;
  handleSkipDocument: () => void;
  allowedCaptureOptions: DocumentCaptureOption[];
}

const VerifyDocumentsPromptView = (props: VerifyDocumentsPromptViewProps) => {
  const { documentConfig } = props;
  const [t] = useTranslation();
  const { documentPictographPosition } = useContext(ThemeContext);

  if (documentConfig == null) {
    return null;
  }

  const hasExamples = documentConfig.examples != null && documentConfig.examples.length > 0;

  const pictographNode = !hasExamples && (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    <CenteredPictograph height={null} width={null} aria-label='document pictograph'>
      <StyledCustomizablePictograph
        url='documentPictographUrl'
        height='documentPictographHeight'
        vectorEffect='initial'
      >
        <VerificationHero>
          <ImageDocument height={80} width='100%' />
        </VerificationHero>
      </StyledCustomizablePictograph>
    </CenteredPictograph>
  );

  return (
    <Container>
      {documentPictographPosition === PictographPosition.Start && pictographNode}

      <Header>
        <ThemedTitle>{documentConfig.title || t('documents.prompt.title')}</ThemedTitle>
        <ThemedParagraph>
          <Markdown>{documentConfig.prompt || t('documents.prompt.prompt')}</Markdown>
        </ThemedParagraph>
        {hasExamples && (
          <>
            <br />
            <p>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              {t('documents.prompt.examples-prompt', { count: documentConfig.examples.length })}
            </p>
            <DocumentExampleList>
              {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
              {documentConfig.examples.map((example, i) => (
                <DocumentExample
                  data-test='documents-prompt__documents-example'
                  key={`${example}-${i}`}
                >
                  <IconContainer>
                    <IconCircleBullet />
                  </IconContainer>
                  <Markdown>{example}</Markdown>
                </DocumentExample>
              ))}
            </DocumentExampleList>
          </>
        )}
      </Header>

      {documentPictographPosition === PictographPosition.Center && pictographNode}

      <PositionableButtonContainer>
        <CaptureOptions {...props} />
      </PositionableButtonContainer>

      {documentConfig.optional && (
        <ThemedParagraph>
          <Trans i18nKey='documents.prompt.or-skip'>
            Or click
            <a id='document-prompt__skip' onClick={props.handleSkipDocument}>
              here to skip this document
            </a>
            .
          </Trans>
        </ThemedParagraph>
      )}
    </Container>
  );
};

export default VerifyDocumentsPromptView;

const CaptureOptions = (props: VerifyDocumentsPromptViewProps) => {
  return (
    <CaptureOptionsContainer id='document-prompt__view'>
      {props.allowedCaptureOptions.map((option, index) => (
        <OptionBlock key={option} isPrimary={index === 0} option={option} {...props} />
      ))}
    </CaptureOptionsContainer>
  );
};

interface OptionBlockProps {
  handleDrop: (acceptedFiles: File[], rejectedFiles: FileRejection[], event: DropEvent) => void;
  handleDropRejected: (rejectedFiles: FileRejection[]) => void;
  handleSelectCamera: () => void;
  handleInputCapture: (file: Blob) => void;
  isProcessing: PromptOptions;
  documentPromptCaptureButton?: string;
  handleDeviceHandoff: () => void;
  isPrimary: boolean;
  option: DocumentCaptureOption;
}

function OptionBlock(props: OptionBlockProps) {
  const {
    handleDrop,
    handleDropRejected,
    handleSelectCamera,
    handleInputCapture,
    isProcessing,
    documentPromptCaptureButton,
    option,
    handleDeviceHandoff,
  } = props;

  const { getRootProps, getInputProps } = useDropzone({
    accept: ACCEPTED_FILE_FORMATS,
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
    multiple: false,
    disabled: isProcessing != null,
  });

  const [t] = useTranslation();

  switch (option) {
    case DocumentCaptureOption.WebCamera:
      return (
        <OptionButton
          id='document-prompt__button--web-camera'
          buttonType={ButtonType.Primary}
          disabled={isProcessing != null}
          onClick={handleSelectCamera}
          isPending={isProcessing === PromptOptions.SelectCamera}
        >
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          <CaptureButtonContent content={documentPromptCaptureButton} />
        </OptionButton>
      );

    case DocumentCaptureOption.MobileCamera:
      return (
        <ButtonInputCapture
          id='document-prompt__button--mobile-camera'
          data-test='document-prompt__button--mobile-camera'
          buttonType={ButtonType.Primary}
          disabled={isProcessing != null}
          handleSelectImage={handleInputCapture}
          isPending={isProcessing === PromptOptions.UploadImage}
        >
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          <CaptureButtonContent content={documentPromptCaptureButton} />
        </ButtonInputCapture>
      );

    case DocumentCaptureOption.Upload:
      return (
        <OptionButton
          id='document-prompt__button--upload'
          buttonType={props.isPrimary ? ButtonType.Primary : ButtonType.Secondary}
          disabled={isProcessing != null}
          isPending={isProcessing === PromptOptions.UploadImage}
          {...getRootProps()}
        >
          <input
            {...getInputProps()}
            data-test='document-prompt__view--file-upload'
            data-cy='file-upload'
          />
          {t('documents.prompt.upload')}
        </OptionButton>
      );

    case DocumentCaptureOption.ToMobile:
      return (
        <OptionButton
          id='document-prompt__button--to-mobile'
          data-test='document-prompt__button--to-mobile'
          buttonType={props.isPrimary ? ButtonType.Primary : ButtonType.Secondary}
          disabled={isProcessing != null}
          onClick={handleDeviceHandoff}
          isPending={isProcessing === PromptOptions.DeviceHandoff}
        >
          {t('documents.prompt.btn-continue-on-device')}
        </OptionButton>
      );

    default:
      assertNever(option);
  }
}

interface CaptureButtonContentProps {
  content: string;
}

const CaptureButtonContent = ({ content }: CaptureButtonContentProps) => {
  if (content) {
    return <span>{content}</span>;
  } else {
    return <ImgCamera data-test='document-prompt__image--camera' />;
  }
};
